import { useCallback } from "react";
import { useLabels } from "../../../hooks/useLabels";
import { useLogicalSelectedPage, useSelectedZone } from "../../../hooks/useSelection";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { labels } from "../bottom-text-menu/labels";
import { jsonComparer, useMessageToState } from "../../../hooks/useMessageToState";
import { IconName } from "../../icon";
import { useAppContext } from "../../../hooks/useAppContext";
import { IStopPropagation } from "../../../utils/types";
import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";

export function useTextDeleteClearButtons() {
	const [[clearTitle, deleteTitle]] = useLabels([labels.clear, labels.delete]);

	const textZoneDashboard = useTextZoneDashboard();
	const { googleAnalytics } = useAppContext();
	const { busyBlocker } = useBusyBlockerContext();

	const selectedZone = useSelectedZone();
	const page = useLogicalSelectedPage();
	const textZone = eXpress.core.isAnyTextZone(selectedZone) ? selectedZone : undefined;

	const getStateOnMessage = useCallback(() => {
		const isDeleteZoneSupported =
			!page?.definition?.isFixedStyle &&
			page?.editor?.getViewLevel() === eXpress.core.ViewLevel.Advanced;

		return {
			canDeleteZone: textZoneDashboard.canDeleteTextZone(textZone),
			showDeleteZone: textZone?.isEmpty,
			isDeleteZoneSupported,
			canClearTextZone:
				textZoneDashboard.canSetTextValue(textZone) && textZone ? !textZone.isEmpty : false,
		};
	}, [page, textZone, textZoneDashboard]);

	const { canClearTextZone, canDeleteZone, isDeleteZoneSupported, showDeleteZone } =
		useMessageToState({
			name: "useTextClearButton",
			getState: getStateOnMessage,
			comparer: jsonComparer,
		});

	const onDeleteClick = useCallback(
		(e?: IStopPropagation) => {
			e?.stopPropagation();
			if (textZone) textZoneDashboard.deleteTextZone(textZone);

			googleAnalytics.trackEventToGa({
				trackingAction: "TextZoneDeleteClicked",
			});
		},
		[googleAnalytics, textZone, textZoneDashboard],
	);

	const onClearClick = useCallback(
		(e?: IStopPropagation) => {
			e?.stopPropagation();
			if (textZone) {
				busyBlocker
					.during(textZoneDashboard.setTextValueAsync(textZone, ""))
					.catch(() => true);
			}
			googleAnalytics.trackEventToGa({
				trackingAction: "TextZoneClearClicked",
			});
		},
		[textZone, textZoneDashboard, googleAnalytics, busyBlocker],
	);

	return {
		delete: {
			iconName: "bin" satisfies IconName,
			title: deleteTitle,
			isDisabled: !canDeleteZone,
			showDelete: showDeleteZone,
			/** Is supported on Definition */
			isSupported: isDeleteZoneSupported,
			onClick: onDeleteClick,
		},
		clear: {
			iconName: "bin" satisfies IconName,
			title: clearTitle,
			isDisabled: !canClearTextZone,
			onClick: onClearClick,
		},
	} as const;
}
